import {
  JobModel,
  JobsTableSortOrderModel,
} from '@energy-stacks/fleet/feature-jobs-data';
import { ESPageLoadingIndicator, RefetchOnError } from '@energy-stacks/shared';
import { JobsTable } from './JobsTable';
import { DateRange } from '@energy-stacks/core/date-range-picker';
import {
  useESTableServerFilterControls,
  useESTableServerControls,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from '@energy-stacks/core/ui';
import { usePaginatedJobs } from './usePaginatedJobs';
import { JobFilterModel } from './JobsPage';
import { useCallback, useMemo } from 'react';
import { RowSelectionState } from '@tanstack/react-table';
import { compact } from 'lodash-es';

interface JobsProps {
  onJobsSelected: (jobs: JobModel[]) => void;
  selectedJobs: JobModel[] | undefined;
  defaultDateRange: DateRange;
  dateRange?: DateRange | undefined;
  preselectedJobStatusFilters?: JobModel['status'][];
  onDateRangeChange?: (dateRange: DateRange | undefined) => void;
}

const hiddenColumnsIds: (keyof JobModel)[] = ['startDate', 'endDate'];

export const Jobs: React.FC<JobsProps> = ({
  onJobsSelected,
  preselectedJobStatusFilters,
  defaultDateRange,
  onDateRangeChange,
  dateRange,
  selectedJobs,
}) => {
  const { filtersState, setFilters, tableColumnFilters } =
    useESTableServerFilterControls<JobFilterModel>({
      defaultFilters: [
        {
          id: 'startDate',
          value: dateRange || defaultDateRange,
        },
        { id: 'status', value: preselectedJobStatusFilters },
        { id: 'jobType', value: ['INBOUND'] },
      ],
    });

  const { pagination, setPagination, search, setSearch, sorting, setSorting } =
    useESTableServerControls({
      defaultRowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
      initialSorting: [{ id: 'jobId', desc: false }],
    });

  const { data, isError, isFetching, isLoading, refetch } = usePaginatedJobs({
    search: search || undefined,
    pagination: {
      page: pagination.pageIndex,
      size: pagination.pageSize,
    },
    sort:
      sorting.order && sorting.column
        ? ({
            id: sorting.column,
            order: sorting.order,
          } as JobsTableSortOrderModel)
        : undefined,
    startDate: filtersState.startDate?.startDate,
    endDate: filtersState.startDate?.endDate,
    statuses: filtersState.status,
    jobTypes: filtersState.jobType,
  });

  const selectedRows: RowSelectionState = useMemo(() => {
    return (selectedJobs ?? []).reduce((acc, job) => {
      if (!job) {
        return acc;
      }
      return {
        ...acc,
        [job.jobId]: true,
      };
    }, {});
  }, [selectedJobs]);

  const initialRowSelection = useCallback(
    (job: JobModel) =>
      !!selectedJobs?.find((selectedJob) => job?.jobId === selectedJob?.jobId),
    [selectedJobs]
  );

  const onSelectionChange = useCallback(
    (jobsRowSelection: RowSelectionState) => {
      const selectionState = compact(
        Object.keys(jobsRowSelection).map((jobId) => {
          const alreadySelected = selectedJobs?.find(
            (job) => job.jobId === jobId
          );
          return alreadySelected || data?.jobsMap[jobId];
        })
      );

      onJobsSelected(selectionState);
    },
    [data?.jobsMap, onJobsSelected, selectedJobs]
  );

  return (
    <>
      {isLoading ? <ESPageLoadingIndicator /> : null}
      {isError ? <RefetchOnError onRefetch={refetch} /> : null}
      {data && !isError ? (
        <JobsTable
          tableId="create-tour-wizard-jobs-table"
          excludeStatusFilter
          jobs={data}
          enableColumnSelection
          enableRowSelection
          hiddenColumnsIds={hiddenColumnsIds}
          defaultRowSelection={initialRowSelection}
          onSelectionChange={onSelectionChange}
          selectedRows={selectedRows}
          defaultDateRange={defaultDateRange}
          onDateRangeChange={onDateRangeChange}
          setFilters={setFilters}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting.state}
          onSortingChange={setSorting}
          search={search}
          setSearch={setSearch}
          tableColumnFilters={tableColumnFilters}
          isFetching={isFetching}
        />
      ) : null}
    </>
  );
};
